<template>
    <dialog-modal v-if="shouldRender" :show="show" :teleport="true" modal-class="modal-elevated">
        <template #title>
            {{ trans('qr_code_scanner.modal.header') }}
        </template>
        <template #subtitle>
            {{ trans('qr_code_scanner.modal.description') }}
        </template>
        <qr-code-scanner
            v-if="show"
            :key="componentKey"
            :valid="isValidQrCode"
            @failure="onError"
            @reset="isValidQrCode = undefined"
            @success="onScanSuccess"
        />
        <form @submit.prevent="onSuccess(form.curieId)">
            <row class="mt-3">
                <column>
                    <jet-input
                        v-model="form.curieId"
                        :horizontal="false"
                        :invalidFeedback="form.errors.curieId"
                        :label="trans('assets.details.curie_id.label')"
                        :placeholder="trans('assets.details.curie_id.placeholder')"
                        :required="true"
                        append-classes="bg-primary border-primary"
                        class="bg-append-primary"
                    >
                        <template #append>
                            <div class="d-flex align-items-center text-white">
                                <LogoCurieCC :height="16" :width="16" />
                            </div>
                        </template>
                    </jet-input>
                </column>
            </row>
        </form>
        <template #footer>
            <jet-button variant="tertiary" @click="close">{{ trans('general.action.cancel') }} </jet-button>
            <jet-button v-if="form.errors.curieId" :is-processing="form.processing" @click.stop="reset">
                {{ trans('general.action.retry') }}
            </jet-button>
            <jet-button v-else :disabled="!isFormSubmittable" :is-processing="form.processing" @click.stop="onSuccess(form.curieId)">
                {{ primaryButtonText }}
            </jet-button>
        </template>
    </dialog-modal>
</template>

<script>
export const CurieIdScannerType = {
    attachCurieId: 'attachCurieId',
    searchAsset: 'searchAsset',
    scanId: 'scanId',
};
</script>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { router, useForm } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';
import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { trans } from '@/mixins/i18n.js';
import { useCurieIdScanner } from '@/composables/useCurieIdScanner.ts';
import QrCodeScanner from '@/Components/QrCodeScanner.vue';
import JetButton from '@/Jetstream/Button.vue';
import DialogModal from '@/Jetstream/DialogModal.vue';
import JetInput from '@/Jetstream/Input.vue';
import LogoCurieCC from '../../icons/LogoCurieCC.vue';

const emit = defineEmits(['error']);

const { recordScan } = useCurieIdScanner();

const componentKey = ref(0);
const show = ref(false);
const shouldRender = ref(false);
const assetId = ref(null);
const isValidQrCode = ref(undefined);
const scannerType = ref(CurieIdScannerType.searchAsset);
const onSuccessfulScan = ref(null);
// Incremented on every close() / reset to invalidate in-flight responses.
const scanSessionId = ref(0);
let closeTimeoutId = null;

const form = ref(
    useForm({
        curieId: null,
        scannedCode: null,
    }),
);

const primaryButtonText = computed(() => {
    switch (scannerType.value) {
        case CurieIdScannerType.attachCurieId:
            return trans('general.action.save');
        case CurieIdScannerType.searchAsset:
        case CurieIdScannerType.scanId:
            return trans('general.action.go');
    }
});

const isFormSubmittable = computed(() => !form.value.processing && form.value.isDirty);

// Exposed to scanId callers as the `scanner` argument, keeps backward-compat with the
// existing onSuccessfulScan(code, scanner) API that touches form/isValidQrCode/close.
const scannerApi = {
    close: () => close(),
    get isValidQrCode() {
        return isValidQrCode.value;
    },
    set isValidQrCode(value) {
        isValidQrCode.value = value;
    },
    get form() {
        return form.value;
    },
};

function resetForm() {
    form.value = useForm({
        curieId: null,
        scannedCode: null,
    });
    isValidQrCode.value = undefined;
}

function reset() {
    resetForm();
    componentKey.value += 1;
}

function close() {
    // Invalidate any in-flight request tied to the previous scan session.
    scanSessionId.value += 1;
    show.value = false;
    if (closeTimeoutId !== null) {
        clearTimeout(closeTimeoutId);
    }
    closeTimeoutId = setTimeout(() => {
        shouldRender.value = false;
        scannerType.value = null;
        onSuccessfulScan.value = null;
        closeTimeoutId = null;
    }, 300);
}

function onError() {
    emit('error');
}

function onScanSuccess(code) {
    form.value.scannedCode = code;
    onSuccess(code);
}

async function onSuccess(code) {
    form.value.curieId = code;
    form.value.processing = true;

    const wasScannedViaQr = form.value.scannedCode === code;
    const shouldRecord =
        scannerType.value !== CurieIdScannerType.attachCurieId && (wasScannedViaQr || scannerType.value === CurieIdScannerType.scanId);

    if (shouldRecord) {
        try {
            await recordScan(code);
        } catch (error) {
            console.error('Failed to record Curie ID scan', error);
        }
    }

    switch (scannerType.value) {
        case CurieIdScannerType.attachCurieId:
            attachCurieId();
            break;
        case CurieIdScannerType.searchAsset:
            searchAsset();
            break;
        case CurieIdScannerType.scanId:
            onSuccessfulScan.value?.(code, scannerApi);
            break;
    }
}

function searchAsset() {
    const sessionId = scanSessionId.value;
    form.value.processing = true;

    $http
        .get(route('assets.search', { searchTerm: form.value.curieId }))
        .then((resp) => {
            if (sessionId !== scanSessionId.value) return;
            const asset = resp.data;
            if (!asset?.id) {
                isValidQrCode.value = false;
                form.value.errors.curieId = trans('assets.overview.scanner.error.asset_not_found');
                return;
            }
            isValidQrCode.value = true;
            close();
            nextTick(() => {
                router.visit(route('assets.show', asset.id));
            });
        })
        .catch((error) => {
            if (sessionId !== scanSessionId.value) return;
            isValidQrCode.value = false;

            // A 404 is the only genuine "no asset" outcome. Anything else (network failure,
            // server error) is a real fault — show a retryable message instead of the
            // misleading "Tag not found", and flag it so we can see these in the field.
            if (error?.response?.status === 404) {
                form.value.errors.curieId = trans('assets.overview.scanner.error.asset_not_found');
                return;
            }

            form.value.errors.curieId = trans('assets.overview.scanner.error.scan_failed');
            Sentry.captureException(error);
        })
        .finally(() => {
            if (sessionId !== scanSessionId.value) return;
            form.value.processing = false;
        });
}

function attachCurieId() {
    const sessionId = scanSessionId.value;

    form.value.put(route('assets.assign_pid', assetId.value), {
        onSuccess: () => {
            if (sessionId !== scanSessionId.value) return;
            isValidQrCode.value = true;
            setTimeout(close, 300);
        },
        onError: () => {
            if (sessionId !== scanSessionId.value) return;
            isValidQrCode.value = false;
        },
    });
}

const openQrHandler = (config) => {
    // Cancel any pending post-close cleanup so re-opening during the close
    // animation doesn't get its state wiped out by the previous close.
    if (closeTimeoutId !== null) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
    }

    resetForm();
    assetId.value = config?.assetId;

    if (config?.type && !Object.values(CurieIdScannerType).includes(config.type)) {
        throw new Error(`Invalid scanner type ${config.type}`);
    }

    scannerType.value = config?.type || CurieIdScannerType.searchAsset;
    onSuccessfulScan.value = config?.onSuccessfulScan || null;

    shouldRender.value = true;
    nextTick(() => {
        show.value = true;
    });
};

onMounted(() => {
    eventBus.$on(events.openQrCodeScanner, openQrHandler);
});

onBeforeUnmount(() => {
    eventBus.$off(events.openQrCodeScanner, openQrHandler);
    if (closeTimeoutId !== null) {
        clearTimeout(closeTimeoutId);
        closeTimeoutId = null;
    }
});
</script>
